import { Button, makeStyles, Theme, useTheme } from '@material-ui/core';
import { Alert, AlertState, AppAPI, EntityClass, PaginationDataSet, SmartModuleType } from '@moderepo/mode-apis';
import { BaseCompProps, capitalizeAll, ModeTheme, GenericPanelComp } from '@moderepo/mode-app-components';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { AlertSeverity, Entity } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import L, { LatLngLiteral } from 'leaflet';
import { CSSProperties, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { EntityGPSDataSourceSettingsWithAttributes, DEFAULT_ENTITY_GPS_ATTRIBUTE_NAME_LATITUDE, DEFAULT_ENTITY_GPS_ATTRIBUTE_NAME_LONGITUDE,
    getMultipleEntities, isEntityGPSDataSourceSettingsWithAttributes, isEntityGPSDataSourceSettingsWithMetrics,
    DEFAULT_ENTITY_GPS_METRICS_NAME_LATITUDE, DEFAULT_ENTITY_GPS_METRICS_NAME_LONGITUDE, EntityGPSDataSourceSettingsWithMetrics,
    DEFAULT_ENTITY_GPS_METRICS_DEFINITION_ID, isEntityClassUISettings, useSelectTranslatedEntityClasses } from '.';
import { useSelectProject } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { EntityPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { getMetricsDefinitionMetricsId } from './utils';
import { useRitzTranslation } from '../../utils/customHooks';
import { getAlertSeverityDisplayName, RouteHelper } from '..';
import { MapFitBoundsHandler } from '../misc';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        mapContainer: {
            width : '100%',
            height: '100%',
        },

        marker: {
            '& .marker-pin': {
                // Draw shadow
                width       : 2,
                height      : 2,
                borderRadius: '50%',
                position    : 'absolute',
                left        : '50%',
                top         : '50%',
                margin      : '-1px 0 0 -1px',
                boxShadow   : '0 0 6px 10px rgba(0, 0, 0, 0.4)',

                // Drawer marker
                '&:before': {
                    content     : '""',
                    width       : 40,
                    height      : 40,
                    borderRadius: '50% 50% 50% 0',
                    border      : '2px solid #ffffff',
                    position    : 'absolute',
                    transform   : 'rotate(-45deg)',
                    left        : '50%',
                    top         : '50%',
                    margin      : '-45px 0 0 -20px',
                    background  : theme.palette.success.main,
                },

                // Draw white circle
                '&:after': {
                    content     : '""',
                    width       : 26,
                    height      : 26,
                    margin      : '-37px 0 0 -12px',
                    background  : theme.palette.background.paper,
                    position    : 'absolute',
                    borderRadius: '50%',
                },
            },

            // Icon
            '& .alert-count': {
                position  : 'absolute',
                width     : 28,
                fontSize  : 18,
                right     : 0,
                top       : -12,
                left      : -13,
                textAlign : 'center',
                color     : theme.palette.text.primary,
                fontWeight: 'bold',
            },

            '&.severity-none': {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.grey[500],
                    },
                },
            },

            [`&.severity-${AlertSeverity.LOW}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.info.main,
                    },
                },
            },

            [`&.severity-${AlertSeverity.MODERATE}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.warning.main,
                    },
                },
            },

            [`&.severity-${AlertSeverity.HIGH}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.error.main,
                    },
                },
            },
        },

        markerPopup: {
            '& a.leaflet-popup-close-button': {
                color: theme.palette.text.primary,
            },

            '& .leaflet-popup-content-wrapper': {
                overflow: 'hidden',
                padding : 0,
            },

            '& .leaflet-popup-content': {
                padding: 0,
                margin : 0,
            },
        },

        markerPopupContent: {
            minWidth: 200,
        },

        markerPopupHeader: {
            padding   : theme.spacing(1, 3),
            width     : '100%',
            display   : 'inline-block',
            textAlign : 'center',
            fontWeight: 'bold',
            background: theme.palette.success.main,
            color     : theme.palette.success.contrastText,

            [`&.severity-${AlertSeverity.LOW}`]: {
                background: theme.palette.info.main,
                color     : theme.palette.info.contrastText,
            },

            [`&.severity-${AlertSeverity.MODERATE}`]: {
                background: theme.palette.warning.main,
                color     : theme.palette.warning.contrastText,
            },

            [`&.severity-${AlertSeverity.HIGH}`]: {
                background: theme.palette.error.main,
                color     : theme.palette.error.contrastText,
            },
        },

        markerPopupBody: {
            padding: theme.spacing(1),
        },

        markerPopupInfo: {
            display      : 'flex',
            flexDirection: 'row',
            '& .label'   : {
                fontWeight: 'bold',
            },
            '& .value': {
                marginLeft: theme.spacing(1),
            },
        },

        actionsContainer: {
            marginTop     : theme.spacing(1),
            display       : 'flex',
            justifyContent: 'flex-end',
        },
    };
}, { name: 'EntitiesMapContainer', index: 1 });



/**
 * The interface for storing 1 entity's alerts statistic. The key will be the severity # and the value is the alert count for that severity
 * For example:
 *      {
 *          1: 23,      // 23 HIGH alerts
 *          2: 3        // 3 MODERATE alerts
 *          3: 5        // 5 LOW alerts
 *      }
 */
interface AlertsStats {
    [AlertSeverity.HIGH]: number;
    [AlertSeverity.MODERATE]: number;
    [AlertSeverity.LOW]: number;
}


/**
 * Given an entity's alertsStats object, return the highest alert severity. Return undefined if the entity does not have any alert
 */
const getHighestAlertSeverity = (alertsStats: AlertsStats | undefined): AlertSeverity | undefined => {
    if (alertsStats) {
        // If there is at least 1 HIGH alert then the entity's alert state is HIGH
        if (alertsStats[AlertSeverity.HIGH] > 0) {
            return AlertSeverity.HIGH;
        }
        // If there is no HIGH alerts but there is at least 1 MODERATE alert then the entity's alert state is MODERATE
        if (alertsStats[AlertSeverity.MODERATE] > 0) {
            return AlertSeverity.MODERATE;
        }
        // Else if there is at least 1 LOW alert then the entity's alert state is LOW
        if (alertsStats[AlertSeverity.LOW] > 0) {
            return AlertSeverity.LOW;
        }
    }
    return undefined;
};



/**
 * Given an entity and an OPTIONAL ATTRIBUTE GPS data source settings, get the entity's GPS position from the entity's attributes
 */
const getEntityPositionFromAttributes = async (
    entity: Entity,
    gpsDataSourceSettings?: EntityGPSDataSourceSettingsWithAttributes | undefined,
    excludeEntitiesWithoutLocation?: boolean,
): Promise<LatLngLiteral | undefined> => {
    // Get the name of the attributes that are used for latitude and longitude. Use the default name if the settings does not have the names defined
    const latAttrName = gpsDataSourceSettings?.latAttrName ?? DEFAULT_ENTITY_GPS_ATTRIBUTE_NAME_LATITUDE;
    const lngAttrName = gpsDataSourceSettings?.lngAttrName ?? DEFAULT_ENTITY_GPS_ATTRIBUTE_NAME_LONGITUDE;

    // Get the lat/lng attribute values from the entity's attributes if it exist.
    const lat = entity.attributes?.[latAttrName];
    const lng = entity.attributes?.[lngAttrName];

    // If there are NO lat and lng values BUT "excludeEntitiesWithNoLocation" is NOT set to true then return 0, 0 for lat and lng
    if ((lat && lng) || !excludeEntitiesWithoutLocation) {
        return {
            lat: lat && !Number.isNaN(Number(lat)) ? Number(lat) : 0,
            lng: lng && !Number.isNaN(Number(lng)) ? Number(lng) : 0,
        };
    }
    return undefined;
};


/**
 * Given an entity and the Metrics GPS data source settings, get the entity's GPS position from the entity's metrics data.
 * NOTE: metrics can be from different moduleType, "tsdb", "home_app_proxy", etc... For now, we will only support TSDB.
 */
const getEntityPositionFromMetrics = async (
    entity: Entity,
    entityClass: EntityClass,
    gpsDataSourceSettings: EntityGPSDataSourceSettingsWithMetrics,
    excludeEntitiesWithoutLocation?: boolean,
): Promise<LatLngLiteral | undefined> => {

    // Find the metricsDefinition which the entity's GPS position are stored
    const metricsDef = entityClass?.metricsDefinitions?.find((metricsDef) => {
        return metricsDef.metricsDefinitionId === gpsDataSourceSettings.metricsDefinitionId ?? DEFAULT_ENTITY_GPS_METRICS_DEFINITION_ID;
    });

    if (metricsDef && metricsDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB) {
        // Get the collectionId of the collection the metricDef belonged to
        const collectionId = getMetricsDefinitionMetricsId(entity, metricsDef.metricsDefinitionId);

        // fetch the entity's last position by calling the GET TSDB RAW data
        const collectionData = await AppAPI.getInstance().getTimeSeriesCollectionRawData(
            entity.homeId,
            metricsDef.dataStoreConfiguration.moduleInstanceId,
            collectionId,
            (new Date()).toISOString(), -1,
            [
                gpsDataSourceSettings.latMetricName ?? DEFAULT_ENTITY_GPS_METRICS_NAME_LATITUDE,
                gpsDataSourceSettings.lngMetricName ?? DEFAULT_ENTITY_GPS_METRICS_NAME_LONGITUDE,
            ],
            [],
        );

        // collectionData will be an array of data row, 1 for each timestamp. We only need to use the LAST data row which should contain the
        // entity's most recent position.
        if (collectionData.data && collectionData.data.length > 0) {
            const lastRow = collectionData.data[collectionData.data.length - 1];

            // For each row, there will be at least 3 columns of data. The first column will be the timestamp, and the other 2 columns are for
            // the lat and lng values
            if (lastRow && lastRow.length > 2 && typeof lastRow[1] === 'number' && typeof lastRow[2] === 'number') {
                return {
                    lat: Number(lastRow[1]),
                    lng: Number(lastRow[2]),
                };
            }
        }
    }

    // If there are NO lat and lng values BUT "excludeEntitiesWithNoLocation" is NOT set to true then return 0, 0 for lat and lng
    if (!excludeEntitiesWithoutLocation) {
        return {
            lat: 0,
            lng: 0,
        };
    }

    return undefined;
};



export interface EntitiesMapContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    readonly title?: string | undefined;
    readonly icon?: string | undefined;
    readonly customSecondaryActions?: BaseCompProps['customSecondaryActions'];
    readonly infoCompProps?: {
        readonly className?: BaseCompProps['className'] | undefined;
    } | undefined;

    readonly entityIds: readonly string[];

    // Whether or not the component should show the entities' alert state
    readonly showAlertingState?: boolean | undefined;

    // Whether or not the component should exclude entities that have no location data. The default is false, show all entities.
    readonly excludeEntitiesWithoutLocation?: boolean | undefined;

    readonly onEntitySelected?: ((entity: Entity)=> void) | undefined;
}


export const EntitiesMapContainer: React.FC<EntitiesMapContainerProps> = (props: EntitiesMapContainerProps) => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const classes = useStyle();
    const theme = useTheme() as ModeTheme;

    // The array of entity objects for the given props.entityId
    const [entityObjects, setEntityObjects] = useState<readonly Entity[] | undefined>();

    // The state where the position for each entity is stored. This will be a map of entityId => {lat, lng}
    const [entitiesPositionByEntityId, setEntitiesPositionByEntityId] = useState<{readonly [entityId: string]: LatLngLiteral | undefined}>();

    // The state where the alerts statistic for each entity is stored. This will be a map of entityId => AlertsStats
    const [alertsStatsByEntityId, setAlertsStatsByEntityId] = useState<{ readonly [entityId: string]: AlertsStats | undefined }>();


    /**
     * Given an array of entities, get the GPS position for each entity and return a map of entityId => {lat, lng} objects.
     */
    const fetchAllEntitiesPosition = useCallback(async (
        entities: readonly Entity[],
        excludeEntitiesWithNoLocation?: boolean,
    ) => {
        const result: {
            [entityId: string]: LatLngLiteral | undefined
        } = {};

        // Create an array of GET entity position API calls but don't make the call yet
        const fetchActions: Promise<LatLngLiteral | undefined>[] = [];

        // For each entity, create an action to fetch its' GPS data and add the action to the fetchActions array
        entities.forEach((entity) => {

            // Check if there is a GPS data source settings defined in the entity's entityClass
            const entityClass = entityClasses?.find((entityClass) => {
                return entityClass.entityClass === entity.entityClass;
            });
            const entityUISettings = entityClass?.displaySettings.uiSettings;
            const gpsDataSourceSettings = isEntityClassUISettings(entityUISettings) ? entityUISettings.gpsDataSourceSettings : undefined;

            if (gpsDataSourceSettings) {
                // If there is a GPS data source settings then fetch GPS data based on the settings
                if (isEntityGPSDataSourceSettingsWithAttributes(gpsDataSourceSettings)) {
                    fetchActions.push(getEntityPositionFromAttributes(entity, gpsDataSourceSettings, excludeEntitiesWithNoLocation));
                } else if (isEntityGPSDataSourceSettingsWithMetrics(gpsDataSourceSettings) && entityClass) {
                    fetchActions.push(getEntityPositionFromMetrics(entity, entityClass, gpsDataSourceSettings, excludeEntitiesWithNoLocation));
                }
            } else {
                // If there is no settings that tells the component how to get the entity's GPS coordinate, assume the entity's coordinate
                // is set in the entity's attributes
                fetchActions.push(getEntityPositionFromAttributes(entity, undefined, excludeEntitiesWithNoLocation));
            }
        });

        // Make all the API calls in 1 Promise
        const responses = await Promise.allSettled(fetchActions);

        responses.forEach((response, index) => {
            // For each SUCCESSFUL api calls, get the entity position data from the response and add them to the result
            const entity = entities[index];
            if (response.status === 'fulfilled' && response.value) {
                result[entity.entityId] = response.value;
            }
        });

        return result;
    }, [entityClasses]);



    /**
     * Given an array of entityIds, fetch ACTIVE alerts for each entity that has the specified severity. Then, add the alerts count to the
     * provided "result" object.
     */
    const fetchAllEntitiesAlertsStatsForOneSeverity = useCallback(async (
        entityIds: readonly string[],
        severity: AlertSeverity,
        result: {
            [entityId: string]: AlertsStats;
        },
    ) => {

        // Create an array of GET alerts API calls but don't make the call yet
        const fetchActions: Promise<PaginationDataSet<Alert>>[] = [];
        entityIds.forEach((entityId) => {
            fetchActions.push(AppAPI.getInstance().getAlerts(props.projectId, {
                entityId,
                state: AlertState.ALERTING,
                severity,
                limit: 1,                   // Only need to fetch 1 alert. We can get the alerts count from the response header.
            }));
        });

        // Make all the API calls in 1 promise
        const responses = await Promise.allSettled(fetchActions);
        responses.forEach((response, index) => {
            const entityId = entityIds[index];
            // For each SUCCESSFUL api calls, get the alerts count from the response.range
            if (response.status === 'fulfilled' && result[entityId]) {
                // eslint-disable-next-line no-param-reassign
                result[entityId][severity] = response.value.range?.total ?? 0;
            }
        });

        return result;
    }, [props.projectId]);


    /**
     * Given an array of entityIds, fetch ACTIVE alerts for each entity and return a map of entityId => AlertsStats
     */
    const fetchAllEntitiesAlertsStats = useCallback(async (entityIds: readonly string[]) => {
        // Create the result object and initialize it with value for all entities' stats
        const result: {
            [entityId: string]: AlertsStats;
        } = entityIds.reduce((result, entityId) => {
            return {
                ...result,
                [entityId]: {
                    high    : 0,
                    moderate: 0,
                    low     : 0,
                },
            };
        }, {});

        // Fetch alerts stats for EACH severity and add the stats to the result.
        await fetchAllEntitiesAlertsStatsForOneSeverity(entityIds, AlertSeverity.HIGH, result);
        await fetchAllEntitiesAlertsStatsForOneSeverity(entityIds, AlertSeverity.MODERATE, result);
        await fetchAllEntitiesAlertsStatsForOneSeverity(entityIds, AlertSeverity.LOW, result);

        return result;
    }, [fetchAllEntitiesAlertsStatsForOneSeverity]);


    /**
     * This useEffect is used for fetching entity objects for all entities specified in props.entityIds when the component loads
     */
    useEffect(() => {
        if (!entityObjects) {
            getMultipleEntities(props.projectId, props.entityIds, appDataState, appDataDispatch).then((result) => {
                setEntityObjects(result);
            });
        }
    }, [appDataDispatch, appDataState, entityObjects, props.entityIds, props.projectId]);



    /**
     * This useEffect is used for fetching alerts for each entity specified in the props.entityIds IF "showAlertingState" is true
     */
    useEffect(() => {
        if (props.showAlertingState) {
            fetchAllEntitiesAlertsStats(props.entityIds).then((result) => {
                setAlertsStatsByEntityId(result);
            });
        }
    }, [fetchAllEntitiesAlertsStats, props.entityIds, props.projectId, props.showAlertingState]);



    /**
     * This useEffect is used for fetching all the entities' position
     */
    useEffect(() => {
        if (entityObjects) {
            fetchAllEntitiesPosition(entityObjects, props.excludeEntitiesWithoutLocation).then((result) => {
                setEntitiesPositionByEntityId(result);
            });
        }
    }, [entityObjects, fetchAllEntitiesPosition, props.excludeEntitiesWithoutLocation]);



    /**
     * On "Show Details" clicks, take the user to the Entity Details page.
     */
    const onShowEntityDetailsHandler = useCallback((entity: Entity) => {
        if (props.onEntitySelected) {
            // If the parent container wants to handle the onEntitySelected event then call the parent container's handler
            props.onEntitySelected(entity);
        } else {
            RouteHelper.getInstance().gotoEntityDetailsPage(props.projectId, entity.entityId);
        }
    }, [props]);


    /**
     * Marker event handler must be an OBJECT with handlers for different events. Therefore, this function will return an OBJECT
     * with the "click" handler since that is the only event we are interested in
     */
    const onMarkerEventClickHandler = useCallback((entity: Entity) => {
        return {
            click: () => {
                if (props.onEntitySelected) {
                    // If the parent container wants to handle the onEntitySelected event then call the parent container's handler
                    props.onEntitySelected(entity);
                } else {
                    // If parent container doesn't handle the event then we will open the preview panel to show the entity details
                    const action: EntityPreviewCompOptions = {
                        location : SlideOutPanelLocation.RIGHT,
                        compType : SlideOutPanelCompType.ENTITY_PREVIEW,
                        compProps: {
                            data: {
                                projectId: props.projectId,
                                entityId : entity.entityId,
                            },
                            enableCreateChildren: true,
                            enableDeleteEntity  : true,
                        },
                    };
                    uiDispatch(showSlideOutPanel(action));
                }
            },
        };
    }, [props, uiDispatch]);


    /**
     * Get all the markers position and cache them. We will use these to set the map's bounds to fit the markers
     */
    const markersPosition = useMemo(() => {
        if (entitiesPositionByEntityId) {
            return Object.values(entitiesPositionByEntityId).reduce((result: readonly LatLngLiteral[], latLng) => {
                if (latLng) {
                    return [...result, latLng];
                }
                return result;
            }, []);
        }
        return undefined;
    }, [entitiesPositionByEntityId]);


    const mapCenter = useMemo(() => {
        return { lat: 0, lng: 0 };
    }, []);

    return (
        <GenericPanelComp
            className={clsx(props.className, classes.root)}
            style={props.style}
            title={props.title ?? capitalizeAll(trans('terms.entity', { count: 2 }))}
            icon={props.icon ?? trans('icons.entity')}
            customSecondaryActions={props.customSecondaryActions}
        >
            <MapContainer className={clsx(classes.mapContainer)} center={mapCenter} zoom={5}>
                <TileLayer
                    attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
                    url={`${theme.mapTilesURL}?key=0FuuQf9TW9Dp1F7i0rsp`}
                />
                <MapFitBoundsHandler
                    positions={markersPosition}
                    updateBoundOnce={true}
                />

                {/*
                    Create a Marker pin for each entity
                */}
                {entityObjects && entityObjects.map((entity, index) => {
                    // Get the entity's position
                    const entityPosition = entitiesPositionByEntityId?.[entity.entityId];

                    if (!entity || !entityPosition) {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <Fragment key={`${index}`} />
                        );
                    }


                    // Get the entity's alertsStats and then find the highest alert severity from the alertsStats
                    const alertsStats = alertsStatsByEntityId?.[entity.entityId];
                    const entitySeverity = getHighestAlertSeverity(alertsStats) ?? 'none';

                    // Create an Alert Count to be displayed in the marker only if the entity has some alerts AND props.showAlertingState is true
                    const alertCount = props.showAlertingState
                        ? (alertsStats?.[AlertSeverity.HIGH] ?? 0)
                            + (alertsStats?.[AlertSeverity.MODERATE] ?? 0)
                            + (alertsStats?.[AlertSeverity.LOW] ?? 0)
                        : '';
                    const alertCountHtml = `<span class="alert-count">${alertCount}</span>`;

                    const markerIcon = L.divIcon({
                        className  : clsx(classes.marker, props.showAlertingState && `severity-${entitySeverity}`),
                        html       : `<div class="marker-pin"></div>${alertCountHtml}`,
                        iconSize   : [2, 54],
                        iconAnchor : [0, 30],
                        popupAnchor: [1, -42],
                    });


                    return (
                        <Marker
                            key={`${entity.entityId}`}
                            position={entityPosition}
                            icon={markerIcon}
                            eventHandlers={!props.showAlertingState ? onMarkerEventClickHandler(entity) : undefined}
                        >
                            {props.showAlertingState && (
                                <Popup
                                    className={classes.markerPopup}
                                >
                                    <div className={classes.markerPopupContent}>
                                        <span className={clsx(classes.markerPopupHeader, props.showAlertingState && `severity-${entitySeverity}`)}>
                                            {entity.displaySettings.name}
                                        </span>
                                        <div className={classes.markerPopupBody}>
                                            <div className={classes.markerPopupInfo}>
                                                <span className="label">{capitalizeAll(trans('alerting.label.alert', { count: 2 }))}:</span>
                                                <span className="value">
                                                    {/**
                                                     * If there are at least 1 alerts, show the count of each alert type
                                                     */}
                                                    {alertsStats && alertCount > 0 ? (
                                                        [AlertSeverity.HIGH, AlertSeverity.MODERATE, AlertSeverity.LOW].map((severity) => {
                                                            const alertCount = alertsStats[severity];
                                                            if (alertCount !== undefined && alertCount > 0) {
                                                                return (
                                                                    <div key={severity}>
                                                                        {alertCount} {trans(getAlertSeverityDisplayName(severity))}
                                                                    </div>
                                                                );
                                                            }
                                                            return <Fragment key={severity} />;
                                                        })
                                                    ) : (
                                                        // Show 0 alerts
                                                        0
                                                    )}
                                                </span>
                                            </div>

                                            <div className={classes.actionsContainer}>
                                                <Button
                                                    size="small"
                                                    color="default"
                                                    onClick={() => {
                                                        onShowEntityDetailsHandler(entity);
                                                    }}
                                                >
                                                    {capitalizeAll(trans('label.view_details'))}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Popup>
                            )}
                        </Marker>
                    );
                })}
            </MapContainer>
        </GenericPanelComp>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntitiesMapContainer', EntitiesMapContainer);
